import useWorkspaceSelected from 'Hooks/useWorkspaceSelected';
import FirstStepsContext from './FirstStepsContext';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { ROUTES } from 'Utils/constants';
import { completeWorkspaceStep } from 'Services/user';
import { useNavigate } from 'react-router-dom';
import { confetti } from 'Utils/confetti';
import { useTranslation } from 'react-i18next';
import Icon from 'Components/Atoms/Icon/Icon';
import OutlineScan from 'Components/Atoms/OutlineIcons/OutlineScan';

export const GROUP_ROUTE_MAP = {
    issue_invoices: ROUTES.firstStepsInvoicesIssue,
    manage_invoices: ROUTES.firstStepsInvoicesUpload,
    monitor_finances: ROUTES.firstStepsProvidersConnect,
};

const GROUP_STEPS = {
    issue_invoices: [
        'project_workspace_created',
        'introduction_video_watched',
        'project_workspace_invoicing_data_configured',
        'contact_created',
        'payment_method_created',
        'project_workspace_tax_authority_selected',
        'invoice_issued',
    ],
    manage_invoices: [
        'project_workspace_created',
        'introduction_video_watched',
        'project_workspace_invoicing_data_configured',
        'invoice_uploaded_from_integration',
        'invoice_validated',
        'invoice_exported',
    ],
    monitor_finances: [
        'project_workspace_created',
        'introduction_video_watched',
        'account_created',
        'taxonomy_tag_created',
        'taxonomy_tag_keyword_added',
        'dashboard_created',
        'dashboard_module_added',
    ],
};

export default function FirstStepsProvider({ children }) {
    const { workspace, isLoading, updateWorkspace } = useWorkspaceSelected();
    const [firstStepsCompleted, setFirstStepsCompleted] = useState(workspace.first_steps_completed ?? []);
    const { t } = useTranslation('first-steps');

    const navigate = useNavigate();

    const isIssueInvoicesCompleted = GROUP_STEPS.issue_invoices.every((step) => firstStepsCompleted.includes(step));
    const isManageInvoicesCompleted = GROUP_STEPS.manage_invoices.every((step) => firstStepsCompleted.includes(step));
    const isMonitorFinancesCompleted = GROUP_STEPS.monitor_finances.every((step) => firstStepsCompleted.includes(step));

    const groups = [
        {
            id: 'issue_invoices',
            title: t('issue-invoices-title'),
            description: t('issue-invoices-description'),
            completed: isIssueInvoicesCompleted,
            icon: <Icon name="invoice-delivered" size={20} color="currentColor" className="translate-x-0.5" />,
            iconClassName: 'bg-primary/10 text-primary',
        },
        {
            id: 'manage_invoices',
            title: t('manage-invoices-title'),
            description: t('manage-invoices-description'),
            completed: isManageInvoicesCompleted,
            icon: <OutlineScan size={20} color="currentColor" />,
            iconClassName: 'bg-income/10 text-income',
        },
        {
            id: 'monitor_finances',
            title: t('monitor-finances-title'),
            description: t('monitor-finances-description'),
            completed: isMonitorFinancesCompleted,
            icon: <Icon name="nav-providers" size={22} color="currentColor" />,
        },
    ];

    useEffect(() => {
        if (!isLoading) {
            setFirstStepsCompleted(workspace.first_steps_completed ?? []);
        }
    }, [isLoading]);

    useEffect(() => {
        const from = sessionStorage.getItem('redirect');
        if (!from) return;
        if (from.startsWith(ROUTES.firstSteps) && workspace?.first_steps_is_hidden) {
            sessionStorage.removeItem('redirect');
        }
    }, [workspace?.first_steps_is_hidden]);

    const setCurrentGroupId = (groupId) => {
        updateWorkspace({ first_steps_group: groupId });
    };

    const handleCompleteStep = (stepId) => {
        setFirstStepsCompleted((prev) => {
            if (prev.includes(stepId)) {
                return prev;
            }

            const updated = [...prev, stepId];
            completeWorkspaceStep(stepId);
            return updated;
        });
    };

    const handleCompleteGroup = (_groupId) => {
        setCurrentGroupId(null);

        const hasCompletedAllSteps = Object.values(GROUP_STEPS)
            .flat()
            .every((step) => firstStepsCompleted.includes(step));

        if (hasCompletedAllSteps) {
            handleCompleteAllSteps();
        } else {
            navigate(ROUTES.firstSteps);

            setTimeout(() => {
                confetti();
            }, 500);
        }
    };

    const handleNavigateToGroup = (groupId) => {
        const route = GROUP_ROUTE_MAP[groupId];
        if (!route) return;

        setCurrentGroupId(groupId);
        navigate(route);
    };

    const handleCompleteAllSteps = () => {
        updateWorkspace({ first_steps_is_hidden: true });
        navigate(ROUTES.dashboard);
    };

    const handleReactivateFirstSteps = () => {
        setCurrentGroupId(null);
        updateWorkspace({ first_steps_is_hidden: false });
    };

    if (isLoading) {
        return null;
    }

    const value = {
        isFirstStepsHidden: workspace.first_steps_is_hidden,
        firstStepsCompleted,
        currentGroupId: workspace.first_steps_group ?? null,
        completeStep: handleCompleteStep,
        completeGroup: handleCompleteGroup,
        navigateToGroup: handleNavigateToGroup,
        completeAllSteps: handleCompleteAllSteps,
        reactivateFirstSteps: handleReactivateFirstSteps,
        groups,
    };

    return <FirstStepsContext.Provider value={value}>{children}</FirstStepsContext.Provider>;
}

FirstStepsProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
